'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTakeoverPrice } from '@/hooks/use-takeover-price';
import type { LobbyGridPage } from '@/hooks/use-lobby-grid-page';
import { useGridHistory } from '@/hooks/use-grid-history';
import { tokenToUsd } from '@/hooks/use-token-prices';
import { getBoardroomLiveGridId, resolveLobbyLiveGridId } from '@/features/lobby/data/boardroom-grid';
import { TAKEOVER_TOKEN_ADDRESS } from '@/lib/constants/addresses';
import { gridIdToPath, truncateAddress } from '@/lib/utils';
import type { SaleAnnotation, TimeRange } from '@/components/grid-v2/floor-price-chart';
import type { RewardsTimeframe } from '@/components/grid-v2/rewards-tab';
import type { V2GridSeatEvent } from '@/lib/types/v2';
import { getTaxTokenConfig } from '@/lib/drawer-utils';

/** Same as grid detail `page.tsx` — floor chart markers / volume from creator + secondary buyouts only. */
const SALE_ANNOTATION_TYPES = new Set(['BOUGHT_FROM_CREATOR', 'BOUGHT_OUT']);

/** Same row shape as `GET /api/grid/.../events-chart`. */
type GridEventsChartRow = {
  id: string;
  eventType: string;
  user: string;
  timestamp: number;
  price: string | null;
  newPrice: string | null;
  oldHolder: string | null;
  seatId: string;
};

function rowsToV2Events(gridId: string, rows: GridEventsChartRow[]): V2GridSeatEvent[] {
  return rows.map((e) => ({
    id: e.id,
    seatId: e.seatId,
    gridId,
    eventType: e.eventType as V2GridSeatEvent['eventType'],
    user: e.user as `0x${string}`,
    timestamp: e.timestamp,
    blockNumber: 0,
    txHash: '',
    price: e.price ?? undefined,
    newPrice: e.newPrice ?? undefined,
    oldHolder: (e.oldHolder ?? undefined) as `0x${string}` | undefined,
  }));
}

/**
 * Chart + feed derivations consumed by the lobby Stats panel. The
 * legacy single-grid page (`/grid/[ugm]/[gridId]`) was retired in
 * favour of the lobby HUD; this hook now owns the canonical Rewards /
 * Tile Sales / Listings derivations directly.
 */
export function useLobbyStatsPanelData(boardId: string | undefined, gridPage: LobbyGridPage) {
  const liveId = resolveLobbyLiveGridId(boardId);
  // Queries fire as soon as we have an ID so the Activity panel's chart +
  // feed data request runs **in parallel** with the main `/api/grid/...`
  // fetch driving `gridPage.grid`. `isLive` (returned below) keeps the
  // stricter gate — the panel still waits for both before swapping the
  // skeleton out, but by then the sub-payloads are usually already here.
  const queriesEnabled = !!liveId;
  const isLive = queriesEnabled && !!gridPage.grid;

  const [rewardsTimeframe, setRewardsTimeframe] = useState<RewardsTimeframe>('7d');
  const [salesRange, setSalesRange] = useState<TimeRange>('7d');

  useEffect(() => {
    setRewardsTimeframe('7d');
    setSalesRange('7d');
  }, [liveId]);

  const {
    pendingYieldDisplay,
    yieldTokenDecimals,
    hasYieldPrice,
    yieldPriceUsd,
    ethPrice,
    takeoverPriceUsdIndexer,
    recentEvents,
    assets,
    grid,
    taxTokenDecimals,
  } = gridPage;

  // Panel-scoped history fetch — the lazy `/history` endpoint (floor-price
  // snapshots + full reward events + daily stats) only loads once the
  // RewardsPanel is mounted, keeping the lobby's first paint lean.
  const history = useGridHistory(liveId ?? undefined, queriesEnabled);
  const floorPriceSnapshots = history.floorPriceSnapshots;
  const rewardEvents = history.rewardEvents;

  // Mirror `useGridPage`'s derivation so the RewardsTab chart sees the same
  // USD-scaled amounts whether it's consumed by the lobby panel or the
  // standalone grid page.
  const rewardEventsUsd = useMemo(() => {
    if (hasYieldPrice) {
      return rewardEvents.map((r) => {
        const decimals = r.adapterType === 'flaunch' ? 18 : yieldTokenDecimals;
        const price = r.adapterType === 'flaunch' ? (ethPrice || yieldPriceUsd) : yieldPriceUsd;
        const usdValue = tokenToUsd(BigInt(r.amount), decimals, price);
        return { ...r, amount: (usdValue * 1e18).toFixed(0) };
      });
    }
    return rewardEvents.map((r) => {
      const decimals = r.adapterType === 'flaunch' ? 18 : yieldTokenDecimals;
      const human = Number(BigInt(r.amount)) / 10 ** decimals;
      return { ...r, amount: (human * 1e18).toFixed(0) };
    });
  }, [rewardEvents, hasYieldPrice, yieldPriceUsd, yieldTokenDecimals, ethPrice]);

  const takeoverPriceInfo = useTakeoverPrice();
  const takeoverPriceUsd = takeoverPriceInfo.priceUsd || takeoverPriceUsdIndexer || 0;

  const primaryCoin = assets[0];
  const coinImageUrl = (primaryCoin?.iconUrl || grid?.imageUrl) ?? null;

  /** Same fallbacks as the `(lobby)` layout's `LobbyClient` so EventFeed classifies TAKEOVER when the indexer omits `taxToken`. */
  const taxTokenForEventFeed = useMemo((): `0x${string}` | undefined => {
    if (!grid) return undefined;
    if (grid.taxToken) return grid.taxToken;
    if (!TAKEOVER_TOKEN_ADDRESS) return undefined;
    const boardroom = getBoardroomLiveGridId();
    if (boardroom && liveId === boardroom) {
      return TAKEOVER_TOKEN_ADDRESS as `0x${string}`;
    }
    const rawDec = grid.taxTokenDecimals;
    if (rawDec != null && rawDec > 6) {
      return TAKEOVER_TOKEN_ADDRESS as `0x${string}`;
    }
    if (taxTokenDecimals > 6) {
      return TAKEOVER_TOKEN_ADDRESS as `0x${string}`;
    }
    return undefined;
  }, [grid, liveId, taxTokenDecimals]);

  /** Match `EventFeed` / `fmtEventPrice` so chart sale prices use indexer decimals + TAKEOVER detection. */
  const saleAnnotationTaxConfig = useMemo(
    () =>
      getTaxTokenConfig(
        taxTokenForEventFeed ?? grid?.taxToken,
        undefined,
        grid?.taxTokenDecimals ?? taxTokenDecimals,
      ),
    [taxTokenForEventFeed, grid?.taxToken, grid?.taxTokenDecimals, taxTokenDecimals],
  );

  const { data: rawChartBuckets, isLoading: chartLoading } = useQuery({
    queryKey: ['grid-rewards-chart', liveId, rewardsTimeframe],
    queryFn: async () => {
      const res = await fetch(`/api/grid/${gridIdToPath(liveId!)}/rewards-chart?window=${rewardsTimeframe}`);
      if (!res.ok) return null;
      const json = await res.json();
      // Runtime guard against a non-array `buckets` (e.g. stale Redis
      // entry from before the rollout's cache shape change). Same class
      // of failure as the events-chart `.map()` crash on 4f0a710.
      const buckets = json?.data?.buckets;
      return Array.isArray(buckets) ? (buckets as { time: number; value: string }[]) : [];
    },
    enabled: queriesEnabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const chartBucketsData = useMemo(() => {
    if (!rawChartBuckets || !Array.isArray(rawChartBuckets)) return null;
    return rawChartBuckets.map((b) => ({
      time: b.time,
      value: hasYieldPrice
        ? (Number(BigInt(b.value)) / 10 ** yieldTokenDecimals) * yieldPriceUsd
        : Number(BigInt(b.value)) / 10 ** yieldTokenDecimals,
    }));
  }, [rawChartBuckets, hasYieldPrice, yieldPriceUsd, yieldTokenDecimals]);

  const { data: fullEventsData } = useQuery({
    queryKey: ['grid-events-chart', liveId],
    queryFn: async (): Promise<GridEventsChartRow[] | null> => {
      const res = await fetch(`/api/grid/${gridIdToPath(liveId!)}/events-chart?window=all`);
      if (!res.ok) return null;
      const json = await res.json();
      // Same runtime guard as `rawChartBuckets` above — protects
      // `rowsToV2Events` and `saleAnnotations` from a non-array body.
      const events = json?.data?.events;
      return Array.isArray(events) ? (events as GridEventsChartRow[]) : null;
    },
    enabled: queriesEnabled,
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  const allV2Events: V2GridSeatEvent[] = useMemo(() => {
    if (!liveId) return recentEvents;
    // Treat an empty array the same as null — an empty API response could be a
    // transient indexer gap; recentEvents (SSE + recent payload) is more reliable.
    if (!fullEventsData || !Array.isArray(fullEventsData) || fullEventsData.length === 0) return recentEvents;
    return rowsToV2Events(liveId, fullEventsData);
  }, [liveId, fullEventsData, recentEvents]);

  const saleAnnotations: SaleAnnotation[] = useMemo(() => {
    // Prefer indexer chart rows, fall back to `recentEvents`. Decimals align with `EventFeed`.
    // Empty array is treated as no data (same as null) — see allV2Events comment above.
    const fullEvents = Array.isArray(fullEventsData) && fullEventsData.length > 0 ? fullEventsData : null;
    const source = fullEvents || recentEvents;
    const divisor = 10 ** saleAnnotationTaxConfig.decimals;
    const out = (source as V2GridSeatEvent[])
      .filter((e) => SALE_ANNOTATION_TYPES.has(e.eventType) && e.price)
      .map((e) => ({
        timestamp: e.timestamp,
        price: Number(e.price) / divisor,
        type: 'sale' as const,
        buyerName: e.userIdentity?.displayName || truncateAddress(e.user, 3),
        buyerAvatar: e.userIdentity?.avatarUrl || undefined,
        sellerName: e.oldHolder ? truncateAddress(e.oldHolder, 3) : undefined,
        sellerAvatar: undefined,
      }));
    return out;
  }, [fullEventsData, recentEvents, saleAnnotationTaxConfig]);

  const rangeToSince = useCallback((r: TimeRange): number => {
    const now = Math.floor(Date.now() / 1000);
    if (r === '24h') return now - 86_400;
    if (r === '7d') return now - 7 * 86_400;
    if (r === '1m') return now - 30 * 86_400;
    return 0;
  }, []);

  const saleEventFeedTypes = useMemo(
    () => ['BOUGHT_FROM_CREATOR', 'BOUGHT_OUT', 'FORFEITED'] as const,
    [],
  );
  const listingEventFeedTypes = useMemo(
    () => ['BOUGHT_VACANT', 'PRICE_UPDATED'] as const,
    [],
  );

  return {
    isLive,
    gridId: liveId ?? '',
    rewardsTimeframe,
    setRewardsTimeframe,
    salesRange,
    setSalesRange,
    chartBucketsData,
    chartLoading,
    rewardEventsUsd,
    pendingYieldDisplay,
    floorPriceSnapshots,
    saleAnnotations,
    allV2Events,
    chartRangeToSince: rangeToSince,
    saleEventFeedTypes: [...saleEventFeedTypes],
    listingEventFeedTypes: [...listingEventFeedTypes],
    taxToken: taxTokenForEventFeed,
    taxTokenDecimals,
    taxTokenDecimalsIndexer: grid?.taxTokenDecimals,
    takeoverPriceUsd,
    coinImageUrl,
    hasYieldPrice,
  };
}
